import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check } from "lucide-react";

export default function Register() {
    const { t, lang } = useI18n();
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ salon_name: "", name: "", email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            await register({ ...form, language: lang });
            nav("/onboarding");
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div className="grid min-h-screen md:grid-cols-2">
            <div className="relative hidden md:block">
                <img src="https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6" alt="Salon" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-primary/50" />
                <div className="absolute bottom-12 left-12 right-12 text-primary-foreground">
                    <h2 className="font-serif text-3xl font-bold">{t("marketing.tagline")}</h2>
                    <ul className="mt-6 space-y-2 text-sm text-primary-foreground/90">
                        {[t("marketing.f1t"), t("marketing.f4t"), t("marketing.f3t")].map((x, i) => (
                            <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" />{x}</li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
                <div className="mx-auto w-full max-w-md">
                    <div className="mb-8 flex items-center justify-between"><Logo /><LanguageSwitcher compact /></div>
                    <span className="inline-block rounded-full bg-accent/12 px-3 py-1 text-xs font-semibold text-accent">{t("auth.trialBadge")}</span>
                    <h1 className="mt-4 font-serif text-3xl font-bold tracking-tight">{t("auth.registerTitle")}</h1>
                    <p className="mt-2 text-muted-foreground">{t("auth.registerSub")}</p>
                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
                        <div><Label>{t("auth.salonName")}</Label><Input required value={form.salon_name} onChange={set("salon_name")} data-testid="register-salon" className="mt-1.5" /></div>
                        <div><Label>{t("auth.yourName")}</Label><Input required value={form.name} onChange={set("name")} data-testid="register-name" className="mt-1.5" /></div>
                        <div><Label>{t("common.email")}</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="register-email" className="mt-1.5" /></div>
                        <div><Label>{t("common.password")}</Label><Input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password" className="mt-1.5" /></div>
                        {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
                        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="register-submit">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.createAccount")}
                        </Button>
                    </form>
                    <p className="mt-6 text-sm text-muted-foreground">
                        {t("auth.haveAccount")} <Link to="/login" className="font-semibold text-accent hover:underline">{t("common.signIn")}</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
